import { Button, Flex, Form, Link, Popup, Radio, Spacing, Text, toast } from '@approvalmax/ui/src/components';
import { domain } from 'modules/data';
import { FC, memo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { notificationService } from 'services/notification';
import { UpdateBudgetSettings, useGetBudgetSettings, useUpdateBudgetSettings } from 'shared/data/webApp/v1';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

import { budgetCheckAmountTypeName } from './SettingsPopupContent.constants';
import { messages } from './SettingsPopupContent.messages';
import SettingsPopupContentSkeleton from './SettingsPopupContent.skeleton';
import { SettingsPopupContentProps } from './SettingsPopupContent.types';

const SettingsPopupContent: FC<SettingsPopupContentProps> = memo((props) => {
    const { toggleIsOpenPopup } = props;

    const company = useCompanyBasedOnUrl();
    const { data, isLoading } = useGetBudgetSettings({
        query: { companyId: company.id },
    });
    const {
        mutate: updateBudgetSettings,
        error: errorUpdateBudgetSettings,
        isLoading: isLoadingUpdateBudgetSettings,
        isSuccess: isSuccessUpdateBudgetSettings,
        isError: isErrorUpdateBudgetSettings,
    } = useUpdateBudgetSettings();

    const form = useForm<UpdateBudgetSettings['body']>({
        defaultValues: {
            companyId: company.id,
        },
    });

    useEffect(() => {
        data?.budgetCheckAmountType && form.setValue(budgetCheckAmountTypeName, data?.budgetCheckAmountType);
    }, [data?.budgetCheckAmountType, form]);

    useEffect(() => {
        if (isSuccessUpdateBudgetSettings) {
            toast.success(messages.setBudgetSettingsSuccessMessage);
            toggleIsOpenPopup(false);
        }

        if (isErrorUpdateBudgetSettings) {
            notificationService.showErrorToast(errorUpdateBudgetSettings.detail);
        }
    }, [
        errorUpdateBudgetSettings?.detail,
        isErrorUpdateBudgetSettings,
        isSuccessUpdateBudgetSettings,
        toggleIsOpenPopup,
    ]);

    const onSubmit = form.handleSubmit((data) => updateBudgetSettings({ body: data }));

    return (
        <Form form={form} onSubmit={onSubmit}>
            <Popup.Header
                title={messages.title}
                actions={
                    <Button type='submit' disabled={isLoadingUpdateBudgetSettings} color='blue80' size='medium'>
                        {messages.saveButton}
                    </Button>
                }
            />

            <Popup.Body>
                {isLoading ? (
                    <SettingsPopupContentSkeleton />
                ) : (
                    <>
                        <Flex justifyContent='space-between'>
                            <Text font='body' fontWeight='medium'>
                                {messages.amountSectionTitle}
                            </Text>

                            <Link
                                font='body'
                                href='https://support.approvalmax.com/portal/en/kb/articles/how-to-set-up-budget-checking-with-gross-or-net-amounts-quickbooks-online'
                                external
                            >
                                {messages.learnMore}
                            </Link>
                        </Flex>

                        <Spacing height={16} />

                        <Radio.Group
                            name={budgetCheckAmountTypeName}
                            required
                            disabled={isLoadingUpdateBudgetSettings}
                            block
                        >
                            <Radio value={domain.BudgetCheckAmountType.Gross}>{messages.itemGrossAmount}</Radio>

                            <Radio value={domain.BudgetCheckAmountType.Net}>{messages.itemNetAmount}</Radio>
                        </Radio.Group>
                    </>
                )}
            </Popup.Body>
        </Form>
    );
});

export default SettingsPopupContent;
